import React, { useState, useEffect } from 'react';
import { X, Users, Mail, Percent, Calendar, CheckCircle, XCircle, UserMinus, UserPlus, Plus } from 'lucide-react';
import { Account } from '../../types/account';
import { formatDate } from '../../utils/dateUtils';
import { apiService } from '../../services/api';
import { userService } from '../../services';

interface AccountEmployeesModalProps {
  account: Account;
  onClose: () => void;
  onUpdate?: () => void;
}

export const AccountEmployeesModal: React.FC<AccountEmployeesModalProps> = ({
  account,
  onClose,
  onUpdate
}) => {
  const [employees, setEmployees] = useState(account.user_accounts || []);
  const [unassigning, setUnassigning] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Add Employee form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<Array<{ id: number; name: string; email: string; is_house: boolean }>>([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [newUserId, setNewUserId] = useState<number | ''>('');
  const [newPercentage, setNewPercentage] = useState<string>('');
  const [adding, setAdding] = useState(false);

  const totalPercentage = employees.reduce((sum, ua) => sum + Number(ua.commission_percentage || 0), 0);

  // Keep local state in sync if the parent ever swaps the `account` prop
  // (e.g. when reusing this modal with a different account). When the
  // parent only refetches the wrapper list without changing the prop
  // reference, we self-refresh after Add/Unassign via refreshFromServer().
  useEffect(() => {
    setEmployees(account.user_accounts || []);
  }, [account]);

  // After Add/Unassign, reload the latest assignments for THIS account
  // directly from the API so we don't depend on the parent replacing the
  // account prop (it doesn't — selectedAccountForEmployees keeps its
  // original reference until the user closes and reopens the modal).
  const refreshFromServer = async (): Promise<void> => {
    try {
      const res = await apiService.getAccount(account.id);
      const fresh = (res as any).data;
      if (fresh?.user_accounts) {
        setEmployees(fresh.user_accounts);
      }
    } catch {
      // Non-fatal: parent's onUpdate will eventually refresh on next reopen.
    }
  };

  // Lazy-load active employees (including House) once when the Add form first
  // opens. Round 8 Task 3 made the same change in the upload-flow modal —
  // this fixes the parallel filter in the per-account modal that was missed.
  // House sorts first to match the system-wide House-first convention.
  // The dropdown's selectable list (`selectableUsers` below) is recomputed at
  // render time from the current `employees` array, so a just-unassigned user
  // appears as selectable again without re-running this fetch.
  useEffect(() => {
    if (!showAddForm) return;

    if (!usersLoaded) {
      userService.getUsers({ per_page: 1000, is_active: true })
        .then(res => {
          const list = (res.data || [])
            .map((u: any) => ({
              id: u.id,
              name: u.name,
              email: u.email,
              is_house: !!u.is_house,
            }))
            .sort((a: any, b: any) => {
              if (a.is_house !== b.is_house) return a.is_house ? -1 : 1;
              return a.name.localeCompare(b.name);
            });
          setAvailableUsers(list);
          setUsersLoaded(true);
        })
        .catch(err => {
          setError(err?.response?.data?.message || 'Failed to load employee list');
        });
    }
  }, [showAddForm, usersLoaded]);

  // Filter out anyone already assigned at render time, recomputed whenever
  // employees changes — keeps the dropdown in sync with the current state.
  const assignedUserIds = new Set(employees.map(e => e.user_id));
  const selectableUsers = availableUsers.filter(u => !assignedUserIds.has(u.id));

  const handleAddEmployee = async () => {
    const userId = typeof newUserId === 'number' ? newUserId : parseInt(String(newUserId), 10);
    const pct = parseFloat(newPercentage);

    if (!userId || isNaN(pct) || pct <= 0 || pct > 100) {
      setError('Pick an employee and enter a percentage greater than 0 and at most 100');
      return;
    }

    setAdding(true);
    setError(null);
    try {
      await apiService.bulkAssignEmployeesToAccounts({
        account_ids: [account.id],
        employees: [{ user_id: userId, commission_percentage: pct }],
        overwrite_existing: false,
      });

      const addedName = availableUsers.find(u => u.id === userId)?.name;
      setShowAddForm(false);
      setNewUserId('');
      setNewPercentage('');
      setSuccessMessage(`${addedName ?? 'Employee'} added with ${pct}%`);
      setTimeout(() => setSuccessMessage(null), 3000);

      // Self-refresh so the new row appears immediately, then signal the
      // parent so its surrounding list also reflects the change.
      await refreshFromServer();
      onUpdate?.();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to add employee');
    } finally {
      setAdding(false);
    }
  };

  const handleUnassign = async (userAccountId: number, userName: string) => {
    if (!window.confirm(`Unassign ${userName} from this account?`)) return;

    setUnassigning(userAccountId);
    setError(null);
    try {
      await apiService.bulkRemoveEmployeeFromAccounts([userAccountId], true);
      setEmployees(prev => prev.filter(e => e.id !== userAccountId));
      setSuccessMessage(`${userName} unassigned successfully`);
      setTimeout(() => setSuccessMessage(null), 3000);
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unassign employee');
    } finally {
      setUnassigning(null);
    }
  };

  const handleUnassignAll = async () => {
    if (employees.length === 0) return;
    if (!window.confirm(`Unassign all ${employees.length} employee(s) from this account?`)) return;

    setUnassigning(-1);
    setError(null);
    try {
      const ids = employees.map(e => e.id);
      await apiService.bulkRemoveEmployeeFromAccounts(ids, true);
      setEmployees([]);
      setSuccessMessage('All employees unassigned successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
      onUpdate?.();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to unassign employees');
    } finally {
      setUnassigning(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Assigned Employees</h3>
            <p className="text-sm text-gray-600 mt-1">{account.account_name}</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => { setShowAddForm(s => !s); setError(null); }}
              disabled={unassigning !== null || adding}
              className="flex items-center space-x-1 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors"
              title="Add an employee to this account"
            >
              <UserPlus className="h-4 w-4" />
              <span>Add Employee</span>
            </button>
            {employees.length > 0 && (
              <button
                onClick={handleUnassignAll}
                disabled={unassigning !== null}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm text-red-600 hover:text-red-800 hover:bg-red-50 rounded-md transition-colors"
                title="Unassign all employees"
              >
                <UserMinus className="h-4 w-4" />
                <span>Unassign All</span>
              </button>
            )}
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
            {error}
          </div>
        )}
        {successMessage && (
          <div className="mx-6 mt-4 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
            {successMessage}
          </div>
        )}

        {/* Inline Add Employee form */}
        {showAddForm && (
          <div className="mx-6 mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between mb-3">
              <h4 className="text-sm font-semibold text-blue-900">Add Employee</h4>
              <button
                onClick={() => { setShowAddForm(false); setNewUserId(''); setNewPercentage(''); }}
                className="text-blue-600 hover:text-blue-800"
                aria-label="Cancel"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div className="md:col-span-2">
                <label className="block text-xs text-gray-700 mb-1">Employee</label>
                <select
                  value={newUserId}
                  onChange={(e) => setNewUserId(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                  disabled={adding}
                >
                  <option value="">Select an employee…</option>
                  {selectableUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.is_house ? '[House] ' : ''}{u.name} ({u.email})
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs text-gray-700 mb-1">Commission %</label>
                <input
                  type="number"
                  min={0.01}
                  max={100}
                  step={0.01}
                  placeholder="e.g. 20"
                  value={newPercentage}
                  onChange={(e) => setNewPercentage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
                  disabled={adding}
                />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <button
                onClick={handleAddEmployee}
                disabled={adding || !newUserId || !newPercentage}
                className="flex items-center space-x-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed rounded-md transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>{adding ? 'Adding…' : 'Add'}</span>
              </button>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {employees.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No employees assigned to this account</p>
              <p className="text-sm text-gray-400 mt-1">
                Use the bulk assign or employee management to add team members
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {employees.map((userAccount) => (
                <div
                  key={userAccount.id}
                  className={`border rounded-lg p-4 transition-colors ${
                    userAccount.is_active
                      ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      {/* Employee Info */}
                      <div className="flex items-center space-x-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2">
                            <h4 className="text-sm font-medium text-gray-900">
                              {userAccount.user?.name || 'Unknown User'}
                            </h4>
                            {userAccount.is_active ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Active
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                <XCircle className="h-3 w-3 mr-1" />
                                Inactive
                              </span>
                            )}
                          </div>
                          {userAccount.user?.email && (
                            <div className="flex items-center space-x-1 mt-1">
                              <Mail className="h-3 w-3 text-gray-400" />
                              <p className="text-xs text-gray-600">{userAccount.user.email}</p>
                            </div>
                          )}
                        </div>

                        {/* Commission Percentage */}
                        <div className="flex items-center space-x-2 bg-blue-50 px-3 py-2 rounded-lg">
                          <Percent className="h-5 w-5 text-blue-600" />
                          <div className="text-right">
                            <div className="text-lg font-bold text-blue-900">
                              {Number(userAccount.commission_percentage || 0).toFixed(2)}%
                            </div>
                            <div className="text-xs text-blue-600">Commission</div>
                          </div>
                        </div>

                        {/* Unassign Button */}
                        <button
                          onClick={() => handleUnassign(userAccount.id, userAccount.user?.name || 'Unknown')}
                          disabled={unassigning !== null}
                          className="flex items-center space-x-1 px-2 py-1.5 text-xs text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-colors"
                          title="Unassign employee"
                        >
                          {unassigning === userAccount.id ? (
                            <div className="animate-spin h-3 w-3 border border-red-600 border-t-transparent rounded-full" />
                          ) : (
                            <UserMinus className="h-4 w-4" />
                          )}
                          <span>Unassign</span>
                        </button>
                      </div>

                      {/* Additional Details */}
                      <div className="mt-3 grid grid-cols-2 gap-3 text-xs">
                        {userAccount.start_date && (
                          <div className="flex items-center space-x-1 text-gray-600">
                            <Calendar className="h-3 w-3" />
                            <span>Start: {formatDate(userAccount.start_date)}</span>
                          </div>
                        )}
                        {userAccount.end_date && (
                          <div className="flex items-center space-x-1 text-gray-600">
                            <Calendar className="h-3 w-3" />
                            <span>End: {formatDate(userAccount.end_date)}</span>
                          </div>
                        )}
                      </div>

                      {/* Notes */}
                      {userAccount.notes && (
                        <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-700">
                          <span className="font-medium">Notes:</span> {userAccount.notes}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-4">
            <p className="text-sm text-gray-600">
              {employees.length} {employees.length === 1 ? 'employee' : 'employees'} assigned
            </p>
            {employees.length > 0 && (
              /* Informational only — sum-must-equal-100 rule was removed
                 per client request. Any positive total is valid. */
              <div className="flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-800">
                <Percent className="h-3 w-3" />
                <span>{totalPercentage.toFixed(2)}% Total</span>
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            className="btn btn-secondary"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
